import type { RefObject } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import Sheet from 'components/utils/Sheet'
import LineClockNineIcon from 'LuxKit/Icons/line/LineClockNineIcon'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import type { TripItineraryData, TripDay, TripStop } from 'tripPlanner/api/types'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import { getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import { sendAddPlaceToDay } from './addToTripActions'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Clickable from 'components/Common/Clickable'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import noop from 'lib/function/noop'
import { appendGooglePlaceContext } from 'tripPlanner/utils/googlePlaceContext'
import { getStopDurationLabel } from 'tripPlanner/utils/stopDuration'
import { formatPlacePhrase } from '@luxuryescapes/lib-location-grammar'
const DayIcon = styled(Sheet)`
  white-space: nowrap;
  align-self: flex-start;
`

const DayRow = styled(Clickable)`
  display: block;
  width: 100%;

  ${mediaHoverable} {
    &:hover {
      cursor: pointer;
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    }
  }

  &.current-day {
    background-color: ${props => props.theme.palette.messaging.success.lightBackground};
  }
`

interface TripDayOption {
  dayId: number
  dayNumber: number
  date?: string
  cityName: string
  itemNames: Array<string>
}

interface Props {
  open: boolean
  onClose: () => void
  triggerRef: RefObject<HTMLButtonElement | null>
  anchorRef: RefObject<HTMLButtonElement | null>
  itineraryData?: TripItineraryData
  placeName: string
  placeId?: string
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  sendMessageOptions?: SendMessageOptions
  onAfterAction?: () => void
  initialStop?: TripStop
  currentDayNumber?: number
}

function buildDayOptionsForStop(stop: TripStop): Array<TripDayOption> {
  return stop.days.map((day: TripDay) => ({
    dayId: day.id,
    dayNumber: day.dayNumber,
    date: day.date,
    cityName: stop.placeName,
    itemNames: day.items.map(item => item.name),
  }))
}

function AddToTripDropdown({
  open,
  onClose,
  triggerRef,
  anchorRef,
  itineraryData,
  placeName,
  placeId,
  onSendMessage = noop,
  sendMessageOptions,
  onAfterAction,
  initialStop,
  currentDayNumber,
}: Props) {
  const intl = useIntl()
  const [selectedStop, setSelectedStop] = useState<TripStop | undefined>(initialStop)
  const sendTripActionMessage = useCallback((
    displayMessage: string,
    contextMessage: string,
    options?: SendMessageOptions,
  ) => {
    const taggedMessage = appendContextTag(
      displayMessage,
      appendGooglePlaceContext(contextMessage, placeId),
      TRIP_PLANNER_ACTION_CONTEXT_LABEL,
    )

    onSendMessage(
      taggedMessage,
      options ?? sendMessageOptions,
    )
  }, [onSendMessage, placeId, sendMessageOptions])

  useEffect(() => {
    if (open && initialStop) {
      setSelectedStop(initialStop)
    }
  }, [open, initialStop])

  const stops = useMemo(
    () => itineraryData?.stops ?? [],
    [itineraryData?.stops],
  )

  const dayOptions = useMemo(
    () => selectedStop ? buildDayOptionsForStop(selectedStop) : [],
    [selectedStop],
  )

  const handleStopClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    const stopId = e.currentTarget.dataset.stopId
    if (!stopId) return
    const stop = stops.find(s => String(s.id) === stopId)
    if (!stop) return

    if (stop.days.length === 0) {
      sendTripActionMessage(
        intl.formatMessage(
          {
            id: 'ggiTrOp6wPGTvamHtWR3',
            defaultMessage: 'Add "{placeName}" to be planned for {stopName}',
            description: 'User chat message shown when adding a place to the unscheduled part of a specific trip stop',
          },
          {
            placeName,
            stopName: stop.placeName,
          },
        ),
        `Add "${placeName}" to be planned for ${stop.placeName}`,
      )
      setSelectedStop(undefined)
      onClose()
      onAfterAction?.()
      return
    }

    setSelectedStop(stop)
  }, [intl, onClose, onAfterAction, placeName, sendTripActionMessage, stops])

  const handleNewStopClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    sendTripActionMessage(
      intl.formatMessage({
        id: 'TTIHUsq-eHZIIiTaXMCi',
        defaultMessage: 'Add "{placeName}" to a new stop',
        description: 'User chat message shown when adding a place to a brand new stop in the trip planner',
      }, { placeName }),
      `Add "${placeName}" to a new stop`,
    )
    setSelectedStop(undefined)
    onClose()
    onAfterAction?.()
  }, [intl, onClose, onAfterAction, placeName, sendTripActionMessage])

  const handleDayClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    const dayId = e.currentTarget.dataset.dayId
    const dayNumber = e.currentTarget.dataset.dayNumber
    const cityName = e.currentTarget.dataset.cityName
    const dayDate = e.currentTarget.dataset.dayDate
    if (!selectedStop || !dayId || !dayNumber || !cityName) return

    sendAddPlaceToDay({
      onSendMessage,
      intl,
      placeName,
      placeId,
      stop: selectedStop,
      day: { id: Number(dayId), dayNumber: Number(dayNumber), date: dayDate || undefined },
      sendMessageOptions,
    })
    setSelectedStop(undefined)
    onClose()
    onAfterAction?.()
  }, [intl, onClose, onAfterAction, placeName, placeId, selectedStop, sendMessageOptions, onSendMessage])

  const handleToBePlannedClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    if (!selectedStop?.placeName) return
    sendTripActionMessage(
      intl.formatMessage(
        {
          id: '9aILiyBh-pFYRanJ1VlQ',
          defaultMessage: 'Add "{placeName}" to be planned for {stopName}',
          description: 'User chat message shown when adding a place to the unscheduled part of the selected stop',
        },
        {
          placeName,
          stopName: selectedStop.placeName,
        },
      ),
      `Add "${placeName}" to be planned for ${selectedStop.placeName}`,
    )
    setSelectedStop(undefined)
    onClose()
    onAfterAction?.()
  }, [intl, onClose, onAfterAction, placeName, selectedStop?.placeName, sendTripActionMessage])

  const handleClose = useCallback(() => {
    setSelectedStop(undefined)
    onClose()
  }, [onClose])

  const hasStops = stops.length > 0
  const hasDayOptions = dayOptions.length > 0
  const title = selectedStop
    ? undefined
    : intl.formatMessage({
        id: '-7qRKcIZP3dD-jqgC2np',
        defaultMessage: 'Add to stop',
        description: 'Title for the add to trip dropdown when selecting a stop',
      })

  const backHeader = useMemo(() => {
    if (!selectedStop) return undefined
    return (
      <Heading variant="heading6">
        {intl.formatMessage(
          {
            id: '_bC0-H48OAKz4BlxkgNs',
            defaultMessage: 'Add to {placeName}',
            description: 'Heading showing which stop the item will be added to',
          },
          { placeName: formatPlacePhrase({ location: { name: selectedStop.placeName }, language: intl.locale, intent: 'location' }) },
        )}
      </Heading>
    )
  }, [selectedStop, intl])

  return (
    <DropdownList
      open={open}
      onClose={handleClose}
      triggerRef={triggerRef}
      anchorRef={anchorRef}
      portaled
      title={title}
      headerExtension={backHeader}
      size="S"
    >
      {selectedStop && (
        <>
          <ClickableListItem
            size="medium"
            startIcon={
              <DayIcon padding={8}>
                <LineClockNineIcon />
              </DayIcon>
            }
            title={intl.formatMessage({
              id: 'Z2STA-qQmkEBGkc8Xhc0',
              defaultMessage: 'To be planned',
              description: 'Label for unscheduled trip items option in add to trip dropdown',
            })}
            onClick={handleToBePlannedClick}
          />
          {hasDayOptions && <Divider kind="primary" />}
          {dayOptions.map((day, index) => (
            <React.Fragment key={day.dayId}>
              <DayRow data-day-id={day.dayId} data-day-number={day.dayNumber} data-day-date={day.date ?? ''} data-city-name={day.cityName} onClick={handleDayClick} className={currentDayNumber === day.dayNumber ? 'current-day' : undefined}>
                <Sheet padding="8 16">
                  <Group direction="horizontal" gap={8}>
                    <DayIcon background="light-background" padding={8}>
                      <Caption variant="medium" weight="bold">
                        {getLocalisedDayLabel(intl, day.dayNumber, day.date)}
                      </Caption>
                    </DayIcon>
                    <Group direction="vertical">
                      <BodyText variant="medium">
                        {day.cityName}
                      </BodyText>
                      <Caption variant="medium" lineClamp={2} colour="light-foreground-alt">
                        {day.itemNames.join(' - ')}
                      </Caption>
                    </Group>
                  </Group>
                </Sheet>
              </DayRow>
              {index !== dayOptions.length - 1 && <Divider kind="primary" />}
            </React.Fragment>
          ))}
        </>
      )}
      {!selectedStop && (
        <>
          {stops.map((stop, index) => {
            const durationLabel = getStopDurationLabel(intl, stop)
            return <React.Fragment key={stop.id}>
              <DayRow data-stop-id={stop.id} onClick={handleStopClick}>
                <Sheet padding="8 16">
                  <Group direction="horizontal" gap={8}>
                    {stop.countryFlag && <BodyText variant="large">
                      {stop.countryFlag}
                    </BodyText>}
                    <Group direction="vertical">
                      <BodyText variant="medium">
                        {stop.placeName}
                      </BodyText>
                      {durationLabel && (
                        <Caption variant="medium" colour="light-foreground-alt">
                          {durationLabel}
                        </Caption>
                      )}
                    </Group>

                  </Group>
                </Sheet>
              </DayRow>
              {index !== stops.length - 1 && <Divider kind="primary" />}
            </React.Fragment>
          })}
          {hasStops && <Divider kind="primary" />}
          <DayRow onClick={handleNewStopClick}>
            <Sheet padding="8 16">
              <Group direction="horizontal" gap={8} verticalAlign="center">
                <BodyText variant="medium">
                  <LinePlusIcon />
                </BodyText>
                <BodyText variant="medium">
                  {intl.formatMessage({
                    id: 'S2QbQmfCi-msLqItSdOO',
                    defaultMessage: 'Add new destination',
                    description: 'Menu item to add a new destination stop to the trip',
                  })}
                </BodyText>
              </Group>
            </Sheet>
          </DayRow>
        </>
      )}
    </DropdownList>
  )
}

export default AddToTripDropdown
